<template>
  <SkeletonLoader>
    <div class="pp-skeleton">
      <!-- Left Column -->
      <div class="pp-skeleton__left">
        <!-- Patient Info Card -->
        <div class="pp-skeleton__card pp-skeleton__patient-card">
          <div class="sk-row" style="align-items: center; gap: 16px;">
            <div class="sk-circle sk-circle--lg" style="flex-shrink: 0;"></div>
            <div class="sk-col">
              <div class="sk-line sk-line--lg" style="width: 70%; margin-bottom: 10px;"></div>
              <div style="display: flex; gap: 8px;">
                <div class="sk-rect" style="width: 60px; height: 20px; border-radius: 10px;"></div>
                <div class="sk-rect" style="width: 48px; height: 20px; border-radius: 10px;"></div>
              </div>
            </div>
          </div>
          <div style="display: flex; gap: 10px; padding: 8px 16px 12px;">
            <div v-for="i in 3" :key="'action-' + i" class="sk-rect" :style="{ width: actionWidths[i], height: '32px', borderRadius: '16px' }"></div>
          </div>
        </div>

        <!-- Contact Cards (2x2 grid) -->
        <div class="pp-skeleton__contacts">
          <div v-for="i in 4" :key="'contact-' + i" class="pp-skeleton__contact-card pp-skeleton__card">
            <div class="sk-row" style="padding: 10px 12px;">
              <div class="sk-circle sk-circle--sm" style="flex-shrink: 0;"></div>
              <div class="sk-col">
                <div class="sk-line sk-line--sm" :style="{ width: contactLabelWidths[i] }"></div>
                <div class="sk-line" :style="{ width: contactValueWidths[i] }"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Appointments Table -->
        <div class="pp-skeleton__card pp-skeleton__appointments">
          <div class="pp-skeleton__table-header">
            <div v-for="i in 4" :key="'th-' + i" class="sk-rect" :style="{ width: tableHeaderWidths[i], height: '14px' }"></div>
          </div>
          <div v-for="row in tableRowCount" :key="'row-' + row" class="pp-skeleton__table-row">
            <div v-for="col in 4" :key="'cell-' + row + '-' + col" class="sk-line" :style="{ width: tableCellWidths[row][col], marginBottom: '0' }"></div>
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="pp-skeleton__right">
        <!-- Tab bar -->
        <div class="pp-skeleton__tabs">
          <div v-for="i in 4" :key="'tab-' + i" class="sk-rect" :style="{ width: tabWidths[i], height: '32px', borderRadius: '4px' }"></div>
        </div>

        <!-- Tab content -->
        <div class="pp-skeleton__card pp-skeleton__tab-content">
          <div class="sk-line sk-line--title" style="width: 45%;"></div>
          <div v-for="i in 6" :key="'content-' + i" class="sk-line" :style="{ width: contentLineWidths[i] }"></div>
          <div style="margin-top: 16px;">
            <div class="sk-rect" style="width: 100%; height: 120px; margin-bottom: 12px;"></div>
          </div>
          <div v-for="i in 4" :key="'content2-' + i" class="sk-line" :style="{ width: contentLineWidths2[i] }"></div>
        </div>
      </div>
    </div>
  </SkeletonLoader>
</template>

<script>
import SkeletonLoader from '@/components/SkeletonLoader.vue';

export default {
  name: 'PatientProfileSkeleton',
  components: { SkeletonLoader },
  data() {
    return {
      windowWidth: window.innerWidth,
      actionWidths: { 1: '80px', 2: '72px', 3: '90px' },
      contactLabelWidths: { 1: '40%', 2: '35%', 3: '45%', 4: '38%' },
      contactValueWidths: { 1: '75%', 2: '65%', 3: '70%', 4: '80%' },
      tableHeaderWidths: { 1: '80px', 2: '100px', 3: '70px', 4: '90px' },
      tableCellWidths: {
        1: { 1: '70%', 2: '60%', 3: '50%', 4: '65%' },
        2: { 1: '55%', 2: '75%', 3: '45%', 4: '70%' },
        3: { 1: '65%', 2: '50%', 3: '60%', 4: '55%' },
        4: { 1: '60%', 2: '70%', 3: '55%', 4: '75%' },
        5: { 1: '50%', 2: '65%', 3: '70%', 4: '60%' },
      },
      tabWidths: { 1: '72px', 2: '88px', 3: '64px', 4: '80px' },
      contentLineWidths: { 1: '90%', 2: '75%', 3: '85%', 4: '60%', 5: '80%', 6: '70%' },
      contentLineWidths2: { 1: '85%', 2: '65%', 3: '78%', 4: '55%' },
    };
  },
  computed: {
    tableRowCount() {
      return this.isMobile ? 3 : 5;
    },
    isMobile() {
      return this.windowWidth <= 768;
    }
  },
  created() {
    this._onResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener('resize', this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
  }
};
</script>

<style lang="scss" scoped>
.pp-skeleton {
  display: flex;
  gap: 20px;
  padding: 20px;
}

.pp-skeleton__left {
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pp-skeleton__right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pp-skeleton__card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.pp-skeleton__patient-card {
  padding-top: 4px;
}

.pp-skeleton__contacts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.pp-skeleton__appointments {
  padding: 12px 0;
}

.pp-skeleton__table-header {
  display: flex;
  gap: 16px;
  padding: 8px 16px 12px;
  border-bottom: 1px solid #e2e8f0;
}

.pp-skeleton__table-row {
  display: flex;
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;

  .sk-line {
    flex: 1;
    min-width: 0;
  }

  &:last-child {
    border-bottom: none;
  }
}

.pp-skeleton__tabs {
  display: flex;
  gap: 8px;
  padding: 4px 0;
}

.pp-skeleton__tab-content {
  padding: 20px;
}

@media (max-width: 768px) {
  .pp-skeleton {
    flex-direction: column;
  }

  .pp-skeleton__left {
    width: 100%;
  }

  .pp-skeleton__right {
    display: none;
  }

  .pp-skeleton__contacts {
    grid-template-columns: 1fr;
  }
}
</style>
